import ValidatedField from "@/validation/ValidatedField";
import RtModule from "@/utils/RtModule";
import {isPromise} from "@/utils/Objects";
import FormModel from "@/components/forms/FormModel";
import FormsLoader from "@/components/forms/FormsLoader";

const stringify = require('json-stable-stringify');

const CLIENT_VALIDATION_REMOTE = "remote";
const CLIENT_VALIDATION_LOCAL = "local";
const CLIENT_VALIDATION_HYBRID = "hybrid";

function capitalize(s) {
    return s[0].toUpperCase() + s.slice(1);
}

export default class FormValidatedField extends ValidatedField {
    constructor(/** FormFieldConfig */ formFieldConfig, validatedForm, initialValue) {
        super(validatedForm, initialValue);
        this.formFieldConfig = formFieldConfig;

        this.validationFunction = async (val, valEqToOriginal) => {
            return this.dynamicValidation(val, valEqToOriginal);
        }
        this.readOnly = false;
        this.dynReadOnly = validatedForm.mode !== "NEW";
        this.foreignValueLabel = null;
        this.foreignValueInstance = null;
        this.labelGetter = 'get' + capitalize(this.formFieldConfig.fieldName) + 'Label';
        this.instanceGetter = 'get' + capitalize(this.formFieldConfig.fieldName) + 'Entity';

        /**
         * @type {FormModel|null}
         * @private
         */
        this._masterFormModel = null;


        /**
         * @type {FormModel|null}
         * @private
         */
        this.parentFormModel = null;


        /**
         * @type {HTMLDivElement}
         */
        this._div = null;

        this.renderer = null;

        /**
         * @type {Function|null}
         */
        this.onParentFkChange = null;
        this.visibilityObserverDone = false;
        this.isVisible = true;
        this.readOnlySequence = 0;
        this.computeFieldInProgress = false;

        if (formFieldConfig.onChangeFunction) {
            if (!RtModule().hasFunction(formFieldConfig.onChangeFunction)) {
                Log.warn("No function found:" + formFieldConfig.onChangeFunction, true);
            } else {

                const customFun = RtModule()[formFieldConfig.onChangeFunction];
                // Funkcja przypisana do konkretnego pola
                this.onChangeDelayed((val) => {
                    return customFun(this.validatedForm, val, formFieldConfig);
                })
            }
        }
    }

    set div(val) {
        this._div = val;
        this.setupVisibilityObserver();
    }

    get div() {
        return this._div;
    }

    set masterFormModel(val) {
        this._masterFormModel = val;
        this.setupVisibilityObserver();
    }

    get masterFormModel() {
        return this._masterFormModel;
    }

    setupVisibilityObserver() {
        if (!this.masterFormModel || !this._div) {
            return false;
        }

        if (this.visibilityObserverDone) {
            return false;
        }
        this.visibilityObserverDone = true;

        if (this.formFieldConfig.visibleFunction || this.formFieldConfig.readOnlyFunction) {
            this._masterFormModel.onChange(() => {
                if (!this._masterFormModel.readOnlyMode) {
                    this.checkVisibility();
                }
            });
            this.validatedForm.onChange(() => {
                if (!this.validatedForm.readOnlyMode) {
                    this.checkVisibility();
                }
            });
        }
        return true;
    }

    async checkVisibility() {
        if (!this.validatedForm.originalSource) {
            this.validatedForm.onLoaded(() => this.checkVisibility());
            return;
        }

        this.validatedForm.collectInputs();

        if (this.validatedForm.originalSource.__supplement__?.lockedFields) {
            this.checkReadOnly();
        }

        if (this.formFieldConfig.readOnlyFunction) {
            this.checkReadOnly();
        }

        if (!this.formFieldConfig.visibleFunction) {
            return;
        }

        let inst = this.validatedForm.originalSource;

        let isVisible = await RtModule()[this.formFieldConfig.visibleFunction].call(inst, inst, this.getFormConfig(), this.masterFormModel?.formConfig, this.parentFormModel?.formConfig);
        if (this.isVisible !== isVisible) {
            this.isVisible = isVisible;
            this.validatedForm.validate().then();
            if (!this.isVisible && this._div) {
                this._div.style.opacity = '0%';
                if (this._div.classList.contains("input-fade-in")) {
                    setTimeout(() => {
                        if (!this.isVisible) {
                            this._div.style.display = "none";
                        }
                    }, 300);
                } else {
                    this._div.style.display = "none";
                    this._div.classList.add("input-fade-in");
                }
            } else if (this._div) {
                if (this._div.classList.contains("input-fade-in")) {
                    setTimeout(() => {
                        this._div.style.opacity = '100%';
                    });
                    this._div.style.opacity = '0%';
                    this._div.style.removeProperty('display');
                } else {
                    this._div.style.opacity = '100%';
                    this._div.style.removeProperty('display');
                    this._div.classList.add("input-fade-in");
                }
            }
        }
    }

    checkReadOnly() {
        if (this.formFieldConfig.joined) {
            return;
        }
        let inst = this.validatedForm.originalSource;
        this.readOnlySequence++;

        let lockedFields = this.validatedForm.originalSource.__supplement__?.lockedFields;
        if (lockedFields?.length) {
            for (let lockedField of lockedFields) {
                if (lockedField === this.formFieldConfig.fieldName) {
                    if (this.renderer) {
                        this.renderer.setReadOnly(true);
                    }
                    return;
                }
            }
        }
        if (!this.formFieldConfig.readOnlyFunction) {
            if (this.renderer && typeof this.renderer.setReadOnly === 'function') {
                this.renderer.setReadOnly(this.readOnly || this.dynReadOnly);
            }
            return;
        }
        let isReadOnly = RtModule()[this.formFieldConfig.readOnlyFunction
            ].call(inst, inst, this.getFormConfig(), this.masterFormModel?.formConfig, this.parentFormModel?.formConfig);

        if (isPromise(isReadOnly)) {
            const seq = this.readOnlySequence;

            isReadOnly.then(val => {
                if (seq === this.readOnlySequence) {
                    this.dynReadOnly = val;
                    if (this.renderer) {
                        this.renderer.setReadOnly(this.readOnly || this.dynReadOnly);
                    }
                }
            });
        } else {
            this.dynReadOnly = isReadOnly;
            if (this.renderer) {
                this.renderer.setReadOnly(this.readOnly || this.dynReadOnly);
            }
        }
    }

    isForeignKey() {
        return this.formFieldConfig.isForeignKey;
    }

    getForeignClass() {
        return this.formFieldConfig.foreignClass;
    }

    async getForeignInstance() {
        if (!this.value) {
            return null;
        }

        if (stringify(this.value) === stringify(this.validatedForm?.originalSource[this.formFieldConfig.fieldName])) {
            const src = this.validatedForm?.originalSource;
            if (!src) {
                return null;
            }
            if (src[this.instanceGetter]) {
                return src[this.instanceGetter]();
            } else {
                return null;
            }
        }

        return Rpc.Forms.getEntity(this.formFieldConfig.foreignClass, this.value);
    }

    async getForeignLabel(reset = false) {
        if (!this.value) {
            return "";
        }

        let fix = (data) => {
            const src = this.validatedForm?.originalSource;
            if (src) {
                if (!src.__supplement__) {
                    src.__supplement__ = {};
                }
                if (!src.__supplement__.fkLabels) {
                    src.__supplement__.fkLabels = {};
                }
                src.__supplement__.fkLabels[this.formFieldConfig.fieldName] = data;
            }
            return data;
        }

        if (!reset && stringify(this.value) === stringify(this.validatedForm?.originalSource[this.formFieldConfig.fieldName])) {
            const src = this.validatedForm?.originalSource;
            if (!src) {
                return null;
            }
            if (src.__supplement__?.fkLabels && src.__supplement__?.fkLabels[this.formFieldConfig.fieldName]) {
                src.__supplement__.fkLabels[this.formFieldConfig.fieldName] = undefined;
            }

            if (src[this.labelGetter]) {
                let type = await src[this.labelGetter](true);
                if (!type?.length && this.value?.length) {
                    // console.log('No defined entity label for field {}', this.formFieldConfig.fieldName);
                    return Rpc.Forms.getEntityLabel(this.formFieldConfig.foreignClass, this.value).then(fix);
                }
                fix(type);
                return type;
            } else {
                if (this.validatedForm.formConfig.isView) {
                    return Rpc.Forms.getEntityLabel(this.formFieldConfig.foreignClass, this.value).then(fix);
                }
                return null;
            }
        }

        let res = await Rpc.Forms.getEntityLabel(this.formFieldConfig.foreignClass, this.value).then(fix);
        return res;
    }

    async getTreePath() {
        if (!this.value) {
            return null;
        }

        let fix = (data) => {
            const src = this.validatedForm?.originalSource;
            if (src) {
                if (!src.__supplement__) {
                    src.__supplement__ = {};
                }
                if (!src.__supplement__.treeNodes) {
                    src.__supplement__.treeNodes = {};
                }
                src.__supplement__.treeNodes[this.formFieldConfig.fieldName] = data;
            }
        }

        if (stringify(this.value) === stringify(this.validatedForm?.originalSource[this.formFieldConfig.fieldName])) {
            const src = this.validatedForm?.originalSource;

            if (this.settingInitialValues && src.__supplement__?.treeNodes && src.__supplement__.treeNodes[this.formFieldConfig.fieldName]) {
                return src.__supplement__.treeNodes[this.formFieldConfig.fieldName];
            }
        }

        let pathLabels = await Rpc.Forms.getTreePath(this.formFieldConfig.foreignClass, this.value);
        fix(pathLabels);
        return {pathLabels};
    }

    async getTreePathElements() {
        if (!this.value) {
            return null;
        }

        let fix = (data) => {
            const src = this.validatedForm?.originalSource;
            if (src) {
                if (!src.__supplement__) {
                    src.__supplement__ = {};
                }
                if (!src.__supplement__.pathElements) {
                    src.__supplement__.pathElements = {};
                }
                src.__supplement__.pathElements[this.formFieldConfig.fieldName] = data;
            }
        }
        if (stringify(this.value) === stringify(this.validatedForm?.originalSource[this.formFieldConfig.fieldName])) {
            const src = this.validatedForm?.originalSource;

            if (src.__supplement__?.pathElements) {
                return src.__supplement__.pathElements[this.formFieldConfig.fieldName];
            }
        }

        let pathElements = await Rpc.Forms.getTreePathElements(this.formFieldConfig.foreignClass, this.value);
        fix(pathElements);
        return {pathElements};
    }

    resetFkOptions(newKey) {
        if (this.onParentFkChange) {
            this.onParentFkChange(newKey);
        }
    }

    getParentFkKey() {
        if (!this.isForeignKey()) {
            return null;
        }
        if (this.formFieldConfig.parentField) {
            return this.validatedForm.validatedFieldByName(this.formFieldConfig.parentField).value;
        }
        return null;
    }

    async getForeignOptionsExtended(activeFilters) {
        if (!this.isForeignKey()) {
            return [];
        }

        if (this.formFieldConfig.parentField) {
            let parentKey = this.validatedForm.validatedFieldByName(this.formFieldConfig.parentField).value;
            return Rpc.Forms.listExtendedDictionaryValuesForParent(this.getFormConfig().formClassLong, parentKey,
                this.formFieldConfig.fieldName,
                this.validatedForm?.originalSource);
        }

        if (this.getFormConfig().isView) {
            let rootClassShortId = this.validatedForm.formConfig.rootClassShortId;
            if (rootClassShortId) {
                let masterId = this.validatedForm.originalSource[rootClassShortId];
                let masterClass = this.getFormConfig().masterClass;
                let masterEntity = await Rpc.Forms.getEntity(this.getFormConfig().masterClass, masterId);
                let fieldNameFromView = this.formFieldConfig.fieldName;
                let fieldNameFromMaster = fieldNameFromView.slice(fieldNameFromView.lastIndexOf("_") + 1);
                return Rpc.Forms.listExtendedDictionaryValues(masterClass, fieldNameFromMaster, masterEntity);
            }
        }
        return Rpc.Forms.listExtendedDictionaryValues(this.getFormConfig().formClassLong,
            this.formFieldConfig.fieldName,
            this.validatedForm?.originalSource,
            activeFilters);
    }

    async getForeignOptions(regionCode='def') {
        if (!this.isForeignKey()) {
            return [];
        }

        if (this.formFieldConfig.parentField) {
            let parentKey = this.validatedForm.validatedFieldByName(this.formFieldConfig.parentField).value;
            if (parentKey == null) {
                return [];
            }
            if(regionCode && regionCode !== 'def') {
                return Rpc.Forms.listDictionaryValuesForParentWithRegion(this.getFormConfig().formClassLong, parentKey,
                    this.formFieldConfig.fieldName,
                    this.validatedForm?.originalSource,
                    regionCode);
            }
            return Rpc.Forms.listDictionaryValuesForParent(this.getFormConfig().formClassLong, parentKey,
                this.formFieldConfig.fieldName,
                this.validatedForm?.originalSource);
        }
        if (this.getFormConfig().isView) {
            let rootClassShortId = this.validatedForm.formConfig.rootClassShortId;
            if (rootClassShortId) {
                let masterId = this.validatedForm.originalSource[rootClassShortId];
                let masterClass = this.getFormConfig().masterClass;
                let masterEntity = await Rpc.Forms.getEntity(this.getFormConfig().masterClass, masterId);
                let fieldNameFromView = this.formFieldConfig.fieldName;
                let fieldNameFromMaster = fieldNameFromView.slice(fieldNameFromView.lastIndexOf("_") + 1);
                if(regionCode && regionCode !== 'def') {
                    return Rpc.Forms.listDictionaryValuesWithRegion(masterClass, fieldNameFromMaster, masterEntity, regionCode);
                }
                return Rpc.Forms.listDictionaryValues(masterClass, fieldNameFromMaster, masterEntity);
            }
        } else {
            if(regionCode && regionCode !== 'def') {
                return Rpc.Forms.listDictionaryValuesWithRegion(this.getFormConfig().formClassLong,
                    this.formFieldConfig.fieldName,
                    this.validatedForm?.originalSource, regionCode);
            } else {
                return Rpc.Forms.listDictionaryValues(this.getFormConfig().formClassLong,
                    this.formFieldConfig.fieldName,
                    this.validatedForm?.originalSource);

            }
        }
    }

    valueSet(newVal) {
        if (!this.isForeignKey()) {
            return;
        }

        if (this.formFieldConfig.childrenFields) {
            for (let field of this.formFieldConfig.childrenFields) {
                this.validatedForm.validatedFieldByName(field)?.resetFkOptions(newVal);
            }
        }

    }

    /**
     * @return {FormConfig}
     */
    getFormConfig() {
        return this.validatedForm.formConfig;
    }

    isInUniqueConstraint() {
        return this.formFieldConfig?.inUniqueConstraint;
    }

    async dynamicValidation(val, valEqToOriginal = false) {
        if (!this.isVisible) {
            return true;
        }

        const formField = this.formFieldConfig;
        if (formField.computed) {
            return true;
        }

        /** @type {FormModel} */
        const formModel = this.validatedForm;

        if (!valEqToOriginal || this.mode === "NEW") {
            if (formField.clientValidation === CLIENT_VALIDATION_LOCAL || formField.clientValidation === CLIENT_VALIDATION_HYBRID) {
                if (formField.fieldValidationFunctionName) {
                    if (!RtModule().hasFunction(formField.fieldValidationFunctionName)) {
                        Log.warn("No function found:" + formField.fieldValidationFunctionName, true);
                    } else {

                        const customFun = RtModule()[formField.fieldValidationFunctionName];
                        // Funkcja przypisana do konkretnego pola
                        const customValidationRes = customFun.call(this, val);
                        if (!this.isResultValid(customValidationRes)) {
                            return customValidationRes;
                        }
                    }
                }

                // Funkcja ogolna przypisana do konkretnego typu
                if (formField?.validate && typeof formField["validate"] === 'function') {
                    const jsValidation = await formField.validate(val, this);
                    if (!this.isResultValid(jsValidation)) {
                        return jsValidation;
                    }
                }
            }
            if (formField.clientValidation === CLIENT_VALIDATION_REMOTE || formField.clientValidation === CLIENT_VALIDATION_HYBRID) {
                let id = this.validatedForm.formConfig.isDynamic ? null : formModel?.fields["id"]?.value;
                let res = await Rpc.Forms.validateField(formModel.formConfig.formClassLong, formField.fieldName, val, id);
                if (!this.isResultValid(res)) {
                    return res;
                }
            }
        }
        if (formField.inUniqueConstraint) {
            return await formModel.checkUnique();
        }
        return true;
    }

    async saveField() {
        let entityId = this.validatedForm?.originalSource?.id;
        let data = {};

        const formField = this.formFieldConfig;

        data[formField.fieldName] = this.value;
        data['id'] = entityId;

        let record = null;
        if (this.validatedForm?.originalSource?.recordMetadata?.versionTag) {
            record = await Rpc.Forms.mergingEditEntityFields(
                this.getFormConfig().formClassLong,
                data,
                [formField.fieldName],
                this.validatedForm?.originalSource?.recordMetadata?.versionTag
            ).tag(this.validatedForm.tag).lock();
            if (record?.recordMetadata) {
                this.validatedForm.originalSource.recordMetadata = record.recordMetadata;
            }

        } else {
            record = await Rpc.Forms.editEntityFields(
                this.getFormConfig().formClassLong,
                data,
                [formField.fieldName],
            ).lock();
        }

        if (record?.recordMetadata) {
            this.validatedForm.originalSource.recordMetadata = record.recordMetadata;
        }
        this._value = record[formField.fieldName];
        this.validatedForm.originalSource[formField.fieldName] = this.value;
        this.notify();
    }

    getGenerateFormClassPath(validatedField) {
        return validatedField.masterFormModel?.formConfig?.formClass;
    }
}
